// Types for Admin -> Businesses -> Subscriptions.
//
// This is a DIFFERENT service from the user-facing /api/v2/subscriptions/*
// endpoints modelled in SubscriptionObject.ts: different envelope (see
// ApiEnvelopeHelper's `status` branch), different row shape, and admin scope
// (every business, not the caller's own). The two must not be conflated - in
// particular `billingInterval` here is a WORD ("monthly"), while
// SubscriptionPlan.billingInterval is a NUMBER.

/**
 * One row from GET /api/v1/business-subscriptions/list.
 *
 * Mirrors the wire payload field-for-field. Everything is null-guarded by
 * convertBusinessSubscription so a partial response can never render NaN or
 * crash a card.
 *
 * Note what is NOT here: no businessName, no planName. The row identifies both
 * by uuid only, so the UI has to resolve them (plan via the existing plans
 * catalog, business via getBusinessById) until the backend sends the names.
 */
export interface BusinessSubscription {
  subscriptionId: string;
  businessId: string;
  /** Plan uuid. Expected to match SubscriptionPlan.subscriptionId in the plans
   *  catalog - unverified, so name resolution must fall back to the raw id. */
  planId: string;
  /** Open string ("monthly") - the gateway's word, not a number. */
  billingInterval: string;
  gatewaySubscriptionId: string | null;
  gatewayCustomerId: string | null;
  /** Gateway-hosted mandate/payment link, when one is live. */
  shortUrl: string | null;
  /** Open string by design ("active", "paused", ...). Kept unconstrained for
   *  the same reason as Subscription.state: a closed union would silently
   *  coerce an unknown backend state into the wrong bucket. */
  status: string;
  subscriptionStart: string | null;
  subscriptionEnd: string | null;
  /** Next scheduled charge - the most useful date on the card. */
  chargeAt: string | null;
  endedAt: string | null;
  paidCount: number;
  remainingCount: number;
  totalCount: number;
  /** Smallest currency unit (paise for INR). Format with
   *  SubscriptionPlanFormatter.formatPlanPrice, the same helper the plan
   *  catalog uses, so both screens round money identically. */
  amountPaise: number | null;
  currency: string;
  createdAt: string | null;
  updatedAt: string | null;
  /** True when the subscription is set to stop at the end of the paid cycle -
   *  a cancellation that has not happened yet. Distinct from cancelledAt. */
  cancelAtCycleEnd: boolean;
  cancelledAt: string | null;
  /** e.g. "cashfree". Open string - more gateways are expected. */
  gateway: string;
  cashfreeSessionId: string | null;
  cashfreeSessionExpiry: string | null;
  trialReserved: boolean;
  trialConsumedAt: string | null;
  mandateStart: string | null;
  mandateEnd: string | null;
}

/** Status values the list endpoint is known to accept, lowercase, sent
 *  comma-separated (`?status=active,paused`). NOT exhaustive - the endpoint
 *  takes an open string list, so this is the chip set, not a validation set. */
export const BUSINESS_SUBSCRIPTION_STATUSES = [
  "created",
  "active",
  "paused",
  "authenticated",
  "cancelled",
] as const;

/** What the Subscriptions tab asks for when nothing has been narrowed - just
 *  the newest rows (Sai wants "created" as the sole default, first in the
 *  chip row, rather than active/paused). */
export const DEFAULT_BUSINESS_SUBSCRIPTION_STATUSES: string[] = ["created"];
